/**
 * usePanelKeyboardShortcuts - Hook for keyboard shortcuts to control panel focus and collapse
 */

import { useEffect, useCallback, useRef } from 'react';
import type { PanelSlotId } from '../types';
import type { PanelCollapsed } from '../../types/persistence.types';

export interface UsePanelKeyboardShortcutsOptions {
  /** Whether keyboard shortcuts are enabled */
  enabled?: boolean;

  /** Current focused panel */
  focusedPanel: PanelSlotId | null;

  /** Current collapsed state */
  collapsed: PanelCollapsed;

  /** Panel type (two-panel or three-panel) */
  panelType: 'two-panel' | 'three-panel';

  /** Set focus to a specific panel */
  setFocus: (panel: PanelSlotId) => void;

  /** Callback to expand a panel */
  onExpand: (panel: 'left' | 'right') => void | Promise<void>;

  /** Callback to collapse a panel */
  onCollapse: (panel: 'left' | 'right') => void | Promise<void>;
}

/**
 * Hook for handling Alt+1/2/3 keyboard shortcuts for panel toggle and focus
 *
 * Behavior:
 * - Alt+1 (Left Panel):
 *   1. If collapsed: expand (no focus change)
 *   2. If expanded but not focused: focus it
 *   3. If focused: collapse and focus middle
 *
 * - Alt+2 (Middle Panel):
 *   - Always focus middle (never collapses)
 *
 * - Alt+3 (Right Panel):
 *   1. If collapsed: expand (no focus change)
 *   2. If expanded but not focused: focus it
 *   3. If focused: collapse and focus middle
 */
export function usePanelKeyboardShortcuts(
  options: UsePanelKeyboardShortcutsOptions
): void {
  const {
    enabled = true,
    focusedPanel,
    collapsed,
    panelType,
    setFocus,
    onExpand,
    onCollapse,
  } = options;

  // Use refs to avoid recreating event listener on every render
  const focusedPanelRef = useRef(focusedPanel);
  const collapsedRef = useRef(collapsed);
  const panelTypeRef = useRef(panelType);

  useEffect(() => {
    focusedPanelRef.current = focusedPanel;
  }, [focusedPanel]);

  useEffect(() => {
    collapsedRef.current = collapsed;
  }, [collapsed]);

  useEffect(() => {
    panelTypeRef.current = panelType;
  }, [panelType]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle Alt+Number shortcuts
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      // Use event.code instead of event.key to handle international keyboards
      // event.code gives us 'Digit1', 'Digit2', etc. regardless of keyboard layout
      const code = event.code;
      const currentFocused = focusedPanelRef.current;
      const currentCollapsed = collapsedRef.current;
      const currentPanelType = panelTypeRef.current;

      // Alt+1: Toggle left panel
      if (code === 'Digit1' || code === 'Numpad1') {
        event.preventDefault();

        if (currentCollapsed.left) {
          // State 1: Collapsed -> Expand (no focus change)
          onExpand('left');
        } else if (currentFocused !== 'left') {
          // State 2: Expanded but not focused -> Focus
          setFocus('left');
        } else {
          // State 3: Focused -> Collapse and focus middle
          onCollapse('left');
          setFocus('middle');
        }
      }
      // Alt+2: Focus middle panel
      else if (code === 'Digit2' || code === 'Numpad2') {
        event.preventDefault();
        setFocus('middle');
      }
      // Alt+3: Toggle right panel (only for three-panel layouts)
      else if ((code === 'Digit3' || code === 'Numpad3') && currentPanelType === 'three-panel') {
        event.preventDefault();

        if (currentCollapsed.right) {
          // State 1: Collapsed -> Expand (no focus change)
          onExpand('right');
        } else if (currentFocused !== 'right') {
          // State 2: Expanded but not focused -> Focus
          setFocus('right');
        } else {
          // State 3: Focused -> Collapse and focus middle
          onCollapse('right');
          setFocus('middle');
        }
      }
    },
    [setFocus, onExpand, onCollapse]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}
